import { useState, useEffect, useCallback } from 'react';

export type Theme = 'dark' | 'light';
export type ThemeSetting = 'dark' | 'light' | 'system';

interface UseThemeOptions {
    /** The default theme setting from the scheduling link */
    defaultTheme?: ThemeSetting;
    /** Unique identifier for localStorage (typically the link slug) */
    storageKey?: string;
}

/**
 * Hook to manage theme state for public booking pages
 * 
 * Priority order for theme:
 * 1. URL parameter (?theme=light or ?theme=dark)
 * 2. User preference stored in localStorage (per link)
 * 3. Link's default theme setting
 * 4. Fallback to 'dark'
 * 
 * When defaultTheme is 'system', it respects the user's OS preference
 */
export function useTheme({ defaultTheme = 'dark', storageKey }: UseThemeOptions = {}) {
    const [theme, setTheme] = useState<Theme>('dark');
    const [isInitialized, setIsInitialized] = useState(false);

    const getStorageKey = useCallback(() => {
        return storageKey ? `letsmeet-theme-${storageKey}` : 'letsmeet-theme';
    }, [storageKey]);

    const resolveSystemTheme = useCallback((): Theme => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
        }
        return 'dark';
    }, []);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlTheme = urlParams.get('theme') as Theme | null;
        
        if (urlTheme === 'light' || urlTheme === 'dark') {
            setTheme(urlTheme);
            setIsInitialized(true);
            return;
        }

        const key = getStorageKey();
        const storedTheme = localStorage.getItem(key) as Theme | null;
        
        if (storedTheme === 'light' || storedTheme === 'dark') {
            setTheme(storedTheme);
            setIsInitialized(true);
            return;
        }

        if (defaultTheme === 'system') {
            setTheme(resolveSystemTheme());
        } else {
            setTheme(defaultTheme);
        }
        setIsInitialized(true);
    }, [defaultTheme, getStorageKey, resolveSystemTheme]);

    useEffect(() => {
        if (defaultTheme !== 'system') return;
        
        const key = getStorageKey();
        const storedTheme = localStorage.getItem(key);
        if (storedTheme) return; // User has preference, don't auto-switch

        const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
        
        const handleChange = (e: MediaQueryListEvent) => {
            setTheme(e.matches ? 'light' : 'dark');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [defaultTheme, getStorageKey]);

    useEffect(() => {
        if (!isInitialized) return;

        document.body.classList.remove('theme-light', 'theme-dark');
        
        document.body.classList.add(`theme-${theme}`);

        return () => {
            document.body.classList.remove('theme-light', 'theme-dark');
        };
    }, [theme, isInitialized]);

    const toggleTheme = useCallback(() => {
        const newTheme: Theme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        
        const key = getStorageKey();
        localStorage.setItem(key, newTheme);
    }, [theme, getStorageKey]);

    const setThemeExplicit = useCallback((newTheme: Theme) => {
        setTheme(newTheme);
        
        const key = getStorageKey();
        localStorage.setItem(key, newTheme);
    }, [getStorageKey]);

    const clearPreference = useCallback(() => {
        const key = getStorageKey();
        localStorage.removeItem(key);
        
        if (defaultTheme === 'system') {
            setTheme(resolveSystemTheme());
        } else {
            setTheme(defaultTheme);
        }
    }, [defaultTheme, getStorageKey, resolveSystemTheme]);

    return {
        theme,
        toggleTheme,
        setTheme: setThemeExplicit,
        clearPreference,
        isDark: theme === 'dark',
        isLight: theme === 'light',
        isInitialized
    };
}

export default useTheme;

